import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import ProjectList from './pages/ProjectList';
import ProjectDetail from './pages/ProjectDetail';
import ScheduleLogPage from './pages/ScheduleLogPage';
import LoginPage from './pages/LoginPage';
import UserManagementPage from './pages/UserManagementPage';
import AuditLogPage from './pages/AuditLogPage';
import WorkloadPage from './pages/WorkloadPage';
import ARPage from './pages/ARPage';
import BillingsProjectionPage from './pages/BillingsProjectionPage';
import JobCostDashboardPage from './pages/JobCostDashboardPage';
import DepartmentStatusPage from './pages/DepartmentStatusPage';
import NotificationsPage from './pages/NotificationsPage';
import PreConstructionPage from './pages/PreConstructionPage';
import SettingsPage from './pages/SettingsPage';
import CEActivityPage from './pages/CEActivityPage';
import Sidebar from './components/Sidebar';
import AppHeader from './components/AppHeader';
import ErrorBoundary from './components/ErrorBoundary';
import { StateScreen } from './components/ui';
import { getNotificationCount, getProject, getProjectBySlug, getMe } from './api';
import { can } from './permissions';
import './App.css';

// Project detail tabs use internal keys (some with spaces, kept for back-compat
// with the existing component). URLs use the slug form. Anything not in the map
// is identity (e.g. 'sov' stays 'sov' both ways).
const TAB_KEY_TO_URL = {
  'dashboard': 'overview',
  'contract value': 'contract-value',
  'client view': 'client-view',
};
const TAB_URL_TO_KEY = Object.fromEntries(
  Object.entries(TAB_KEY_TO_URL).map(([key, url]) => [url, key])
);
const tabKeyToUrl = (key) => TAB_KEY_TO_URL[key] || key;
const tabUrlToKey = (url) => TAB_URL_TO_KEY[url] || url;

// Resolves the :idOrSlug URL param to a numeric project id. Accepts either
// the slug (canonical) or a numeric id; numeric ids are redirected to the
// slug URL so links settle on the readable form. The :tab segment drives
// which tab inside ProjectDetail is active.
function ProjectDetailRoute({ user, darkMode, onToggleDarkMode, onLogout }) {
  const { idOrSlug, tab } = useParams();
  const navigate = useNavigate();
  const [projectId, setProjectId] = useState(null);
  const [projectSlug, setProjectSlug] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const isNumeric = /^\d+$/.test(idOrSlug);
    const lookup = isNumeric ? getProject(parseInt(idOrSlug, 10)) : getProjectBySlug(idOrSlug);
    lookup
      .then((p) => {
        if (cancelled) return;
        if (isNumeric && p.slug) {
          // Preserve whatever tab the user is on while swapping id → slug.
          const tabSuffix = tab ? `/${tab}` : '';
          navigate(`/projects/${p.slug}${tabSuffix}`, { replace: true });
          return;
        }
        setProjectId(p.id);
        setProjectSlug(p.slug);
      })
      .catch((e) => { if (!cancelled) setError(e.message || 'Project not found'); });
    return () => { cancelled = true; };
  }, [idOrSlug, tab, navigate]);

  if (error) {
    // Black-screen / raw-text error replaced. The chrome can't render
    // without a valid project, so this is a full-page state.
    return (
      <StateScreen
        variant="full-page"
        kind="not-found"
        title="We couldn't find that project"
        body="It may have been deleted, or the link is out of date. Head back to your projects to pick another one."
        primary={{ label: 'Back to Projects', icon: 'arrow-left', onClick: () => navigate('/projects') }}
        errorCode={`ERROR · 404 · ${idOrSlug}`}
      />
    );
  }
  if (projectId === null) return <div style={{ padding: 24, color: 'var(--text-3)' }}>Loading…</div>;

  const activeTab = tabUrlToKey(tab || 'overview');
  const setActiveTab = (newKey) => {
    navigate(`/projects/${projectSlug || idOrSlug}/${tabKeyToUrl(newKey)}`);
  };

  return (
    <ProjectDetail
      projectId={projectId}
      onBack={() => navigate('/projects')}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      user={user}
      darkMode={darkMode}
      onToggleDarkMode={onToggleDarkMode}
      onLogout={onLogout}
    />
  );
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [darkMode, setDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode ? JSON.parse(savedMode) : false;
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });

  // Auth state
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('e3_user');
    const token = localStorage.getItem('e3_auth_token');
    if (saved && token) {
      try { return JSON.parse(saved); } catch { return null; }
    }
    return null;
  });

  // Listen for auth expiry from API layer
  useEffect(() => {
    const handleAuthExpired = () => { setUser(null); };
    window.addEventListener('auth-expired', handleAuthExpired);
    return () => window.removeEventListener('auth-expired', handleAuthExpired);
  }, []);

  // Refresh the user from the server on mount so the effective `permissions`
  // map is current — the persisted localStorage user may predate the
  // permissions feature or have stale overrides. Errors are ignored (the
  // role-default fallback in can() keeps nav working; a 401 fires auth-expired).
  useEffect(() => {
    if (!localStorage.getItem('e3_auth_token')) return;
    getMe()
      .then((r) => {
        const fresh = r && r.user;
        if (fresh) {
          setUser(fresh);
          localStorage.setItem('e3_user', JSON.stringify(fresh));
        }
      })
      .catch(() => {});
  }, []);

  const handleLogin = (userData) => {
    // Leave the URL alone — if the user was deep-linked to /projects/<slug>/sov,
    // re-rendering with `user` set lets the Routes resolve there directly.
    // For the bare /, the `/` route redirects to /projects automatically.
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('e3_auth_token');
    localStorage.removeItem('e3_user');
    setUser(null);
    // Leave the URL alone — when the user logs back in, handleLogin keeps
    // the URL and the Routes resolve them right back to where they were.
  };

  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem('darkMode', JSON.stringify(newMode));
  };

  const toggleSidebar = () => {
    const newVal = !sidebarCollapsed;
    setSidebarCollapsed(newVal);
    localStorage.setItem('sidebarCollapsed', JSON.stringify(newVal));
  };

  // Accepts the slug (preferred) or a numeric id (back-compat for callers
  // that don't have the project's slug handy). Numeric ids will get
  // redirected to the slug URL by ProjectDetailRoute.
  const openProject = (idOrSlug) => {
    navigate(`/projects/${idOrSlug}/overview`);
  };

  // Notification badge count — poll every 60s
  const [notifCount, setNotifCount] = useState(0);
  useEffect(() => {
    if (!user) return;
    const fetchCount = () => {
      getNotificationCount().then(r => setNotifCount(r.count || 0)).catch(() => {});
    };
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, [user]);

  // If not logged in, show login page
  if (!user) {
    return <LoginPage onLogin={handleLogin} />;
  }

  // Access is capability-based (role defaults ⊕ per-user overrides). Each gate
  // below maps to one capability; see src/permissions.js. Defaults reproduce the
  // old role thresholds, so nothing changes until an override is set.
  const perm = (cap) => can(user, cap);
  const canManageUsers = perm('users.manage');
  const canManageSettings = perm('settings.manage');

  // Are we inside a specific project? /projects/<id> matches, /projects does not.
  const isProjectDetail = /^\/projects\/[^/]+/.test(location.pathname);

  // Sidebar nav items
  const sidebarNav = [
    { key: 'projects', label: 'Dashboard', abbr: 'D', path: '/projects' },
    { key: 'notifications', label: 'Notifications', abbr: 'N', path: '/notifications', badge: notifCount },
    // CE Activity visible to every logged-in user. Server-side
    // role-scope filtering means PMs see only their own jobs;
    // CMs/CDs see their team; VP/Admin see all. The PM use case
    // ("did my Finalize go through to CE?") is precisely why this
    // can't be CM-and-above only.
    { key: 'ce-activity', label: 'CE Activity', abbr: 'CE', path: '/ce-activity' },
    ...(perm('preconstruction.view') ? [{ key: 'pre-construction', label: 'Pre-Construction', abbr: 'PC', path: '/pre-construction' }] : []),
    ...(perm('workload.view') ? [{ key: 'workload', label: 'Workload', abbr: 'W', path: '/workload' }] : []),
    ...(perm('ar.view') ? [{ key: 'ar', label: 'AR / Collections', abbr: 'R', path: '/ar' }] : []),
    ...(perm('billings_projection.view') ? [{ key: 'billings-projection', label: 'Billings Projection', abbr: 'BP', path: '/billings-projection' }] : []),
    ...(perm('jobcosts.view') ? [{ key: 'job-costs', label: 'Job Costs', abbr: 'J', path: '/job-costs' }] : []),
    ...(perm('jobcosts.view') ? [{ key: 'dept-status', label: 'Dept Status', abbr: 'DS', path: '/dept-status' }] : []),
    ...(perm('schedule_log.view') ? [{ key: 'schedule-log', label: 'Schedule Log', abbr: 'S', path: '/schedule-log' }] : []),
    ...(perm('audit.view') ? [{ key: 'audit-log', label: 'Audit Log', abbr: 'A', path: '/audit-log' }] : []),
    ...(canManageUsers ? [{ key: 'user-management', label: 'Team', abbr: 'T', path: '/team' }] : []),
    ...(canManageSettings ? [{ key: 'settings', label: 'Settings', abbr: 'G', path: '/settings' }] : []),
  ];

  // Project-level nav — matches the TABS list inside ProjectDetail so the
  // sidebar and the in-page tab strip stay in sync.
  const projectNav = [
    { key: 'dashboard', label: 'Overview', abbr: 'O' },
    { key: 'sov', label: 'SOV', abbr: 'S' },
    { key: 'tasks', label: 'Schedule', abbr: 'T' },
    { key: 'rollup', label: 'Rollup', abbr: 'R' },
    { key: 'billing', label: 'Billing', abbr: 'B' },
    { key: 'performance', label: 'Performance', abbr: 'P' },
    { key: 'forecast', label: 'Forecast', abbr: 'F' },
    { key: 'contract value', label: 'Contract Value', abbr: 'C' },
    { key: 'job-costs', label: 'Job Costs', abbr: 'J' },
    { key: 'closeout', label: 'Closeout', abbr: 'X' },
    { key: 'client view', label: 'Client View', abbr: 'V' },
  ];

  // When the URL is /projects/<idOrSlug>/<tab>, extract the identifier and
  // tab so sidebar clicks can build new URLs and active highlighting works.
  const projectMatch = location.pathname.match(/^\/projects\/([^/]+)(?:\/([^/]+))?/);
  const currentProjectIdentifier = projectMatch?.[1] || null;
  const currentProjectTab = projectMatch?.[2] ? tabUrlToKey(projectMatch[2]) : 'dashboard';

  const headerTitle = (() => {
    if (isProjectDetail) return 'Project Detail';
    switch (location.pathname) {
      case '/notifications': return 'Notifications';
      case '/ce-activity': return 'CE Activity';
      case '/pre-construction': return 'Pre-Construction Pipeline';
      case '/workload': return 'Team Workload';
      case '/ar': return 'Accounts Receivable';
      case '/billings-projection': return 'Billings Projection';
      case '/job-costs': return 'Job Costs';
      case '/dept-status': return 'Department Status';
      case '/schedule-log': return 'Schedule Change Log';
      case '/team': return 'Team Management';
      case '/audit-log': return 'Audit Log';
      case '/settings': return 'Settings';
      default: return 'Projects Dashboard';
    }
  })();

  return (
    <div className={`app ${darkMode ? 'dark' : ''} ${sidebarCollapsed ? 'sidebar-collapsed' : ''} ${isProjectDetail ? 'project-detail-route' : ''}`}>
      {/* Sidebar is hidden on /projects/:slug/* routes — the project
          chrome (ProjectHeader + TabBar) provides all the nav you need
          inside a project, and the wider canvas helps the Schedule
          Gantt breathe. Dark-mode toggle + Sign out get relocated into
          a menu under the user avatar in ProjectHeader. */}
      {!isProjectDetail && (
        <Sidebar
          sidebarNav={sidebarNav}
          projectNav={projectNav}
          isProjectDetail={isProjectDetail}
          currentProjectIdentifier={currentProjectIdentifier}
          currentProjectTab={currentProjectTab}
          pathname={location.pathname}
          user={user}
          collapsed={sidebarCollapsed}
          onToggleCollapse={toggleSidebar}
          onNavigate={navigate}
          tabKeyToUrl={tabKeyToUrl}
          darkMode={darkMode}
          onToggleDarkMode={toggleDarkMode}
          onLogout={handleLogout}
        />
      )}

      {/* Main Content */}
      <div className="main-content">
        {!isProjectDetail && (
          <AppHeader
            title={headerTitle}
            onOpenProject={openProject}
            onBellClick={() => navigate('/notifications')}
            notifCount={notifCount}
          />
        )}
        {/* On project-detail routes, the redesigned <ProjectHeader />
            inside ProjectDetail.js takes the legacy app-header's place. */}
        <div className="app-main">
          {/* Route-level error boundary — catches render errors anywhere
              inside any route and shows a full-page server-error
              StateScreen instead of a white page. */}
          <ErrorBoundary variant="full-page">
          <Routes>
            <Route path="/" element={<Navigate to="/projects" replace />} />
            <Route path="/projects" element={<ProjectList onSelectProject={openProject} />} />
            <Route path="/projects/:idOrSlug" element={<Navigate to="overview" replace />} />
            <Route
              path="/projects/:idOrSlug/:tab"
              element={
                <ProjectDetailRoute
                  user={user}
                  darkMode={darkMode}
                  onToggleDarkMode={toggleDarkMode}
                  onLogout={handleLogout}
                />
              }
            />
            <Route path="/notifications" element={<NotificationsPage onOpenProject={openProject} currentUser={user} />} />
            <Route path="/ce-activity" element={<CEActivityPage currentUser={user} onOpenProject={openProject} />} />
            <Route path="/pre-construction" element={perm('preconstruction.view') ? <PreConstructionPage onOpenProject={openProject} currentUser={user} /> : <Navigate to="/projects" replace />} />
            <Route path="/workload" element={perm('workload.view') ? <WorkloadPage onOpenProject={openProject} currentUser={user} /> : <Navigate to="/projects" replace />} />
            <Route path="/ar" element={perm('ar.view') ? <ARPage onOpenProject={openProject} currentUser={user} /> : <Navigate to="/projects" replace />} />
            <Route path="/billings-projection" element={perm('billings_projection.view') ? <BillingsProjectionPage onOpenProject={openProject} currentUser={user} /> : <Navigate to="/projects" replace />} />
            <Route path="/job-costs" element={perm('jobcosts.view') ? <JobCostDashboardPage onOpenProject={openProject} currentUser={user} /> : <Navigate to="/projects" replace />} />
            <Route path="/dept-status" element={perm('jobcosts.view') ? <DepartmentStatusPage onOpenProject={openProject} currentUser={user} /> : <Navigate to="/projects" replace />} />
            <Route path="/schedule-log" element={perm('schedule_log.view') ? <ScheduleLogPage onBack={() => navigate('/projects')} onOpenProject={openProject} currentUser={user} /> : <Navigate to="/projects" replace />} />
            <Route path="/team" element={canManageUsers ? <UserManagementPage currentUser={user} /> : <Navigate to="/projects" replace />} />
            <Route path="/audit-log" element={perm('audit.view') ? <AuditLogPage onOpenProject={openProject} /> : <Navigate to="/projects" replace />} />
            <Route path="/settings" element={canManageSettings ? <SettingsPage currentUser={user} /> : <Navigate to="/projects" replace />} />
            <Route path="*" element={
              <StateScreen
                variant="full-page"
                kind="not-found"
                title="This page doesn't exist"
                body="The link you followed might be out of date, or the page may have been moved. Try heading back to your projects."
                primary={{ label: 'Back to Projects', icon: 'arrow-left', onClick: () => navigate('/projects') }}
                errorCode={`ERROR · 404 · ${location.pathname}`}
              />
            } />
          </Routes>
          </ErrorBoundary>
        </div>
      </div>
    </div>
  );
}

export default App;
